import { useCallback, useEffect, useRef, useState } from 'react';

import { AbortError } from '../data/mockApi';
import type { RemoteOption } from '../data/types';

/* =============================================================================
   useRemoteSearch
   -----------------------------------------------------------------------------
   The one implementation of the contract the spec repeats in two places: the
   searchable dropdown (§10 Select) and the table's `select` column filter (§11).

   It guarantees four things, all of which are easy to get subtly wrong:

     1. The search runs on the API side. Nothing here filters a local array.
     2. Requests are debounced, so typing does not fire one call per keystroke.
     3. Nothing is sent until the term reaches `minSearchLength`.
     4. The in-flight request is cancelled before a new one starts, AND a
        response that resolves late is discarded. Cancellation alone is not
        enough — a request that has already resolved cannot be aborted, so the
        sequence number is what actually prevents a stale result overwriting a
        newer one.
   ============================================================================= */

export interface UseRemoteSearchOptions {
  /** The async search. Receives the term and a signal it must honour. */
  search: (term: string, signal: AbortSignal) => Promise<{
    options: RemoteOption[];
    truncated: boolean;
  }>;
  /** Characters required before a request is sent. Default 0. */
  minSearchLength?: number;
  /** Debounce in milliseconds. Default 300. */
  debounceMs?: number;
  /** Debounce applied to the very first load, when the field is untouched. */
  initialLoad?: boolean;
}

export interface UseRemoteSearchResult {
  options: RemoteOption[];
  truncated: boolean;
  loading: boolean;
  error: string | null;
  /** The term actually sent to the API — not the raw input value. */
  query: string;
  setQuery: (term: string) => void;
  /** True while the term is below `minSearchLength`. */
  isBelowMinLength: boolean;
  retry: () => void;
}

export function useRemoteSearch({
  search,
  minSearchLength = 0,
  debounceMs = 300,
  initialLoad = false,
}: UseRemoteSearchOptions): UseRemoteSearchResult {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<RemoteOption[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(initialLoad);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Guards against a resolved-but-stale response. Incremented for every request
  // that is actually issued; a response whose id no longer matches is dropped.
  const requestId = useRef(0);
  // The controller for the request currently in flight.
  const controller = useRef<AbortController | null>(null);
  // The latest `search` identity, so the effect does not re-run when a caller
  // passes an inline arrow function (the common case).
  const searchRef = useRef(search);
  searchRef.current = search;

  const runSearch = useCallback(
    async (value: string) => {
      if (value.length < minSearchLength) {
        // Never leave a previous result on screen while below the threshold.
        controller.current?.abort();
        controller.current = null;
        requestId.current += 1;
        setOptions([]);
        setTruncated(false);
        setLoading(false);
        setError(null);
        return;
      }

      controller.current?.abort();
      const next = new AbortController();
      controller.current = next;
      requestId.current += 1;
      const id = requestId.current;

      setLoading(true);
      setError(null);

      try {
        const result = await searchRef.current(value, next.signal);
        if (id !== requestId.current) return; // a newer request has superseded this one
        setOptions(result.options);
        setTruncated(result.truncated);
        setLoading(false);
      } catch (caught) {
        if (id !== requestId.current) return;
        if (caught instanceof AbortError || next.signal.aborted) return;
        setOptions([]);
        setTruncated(false);
        setError(caught instanceof Error ? caught.message : 'Search failed.');
        setLoading(false);
      }
    },
    [minSearchLength],
  );

  useEffect(() => {
    const isInitial = term === '' && !initialLoad;
    if (isInitial) {
      setLoading(false);
      setOptions([]);
      return;
    }

    const id = window.setTimeout(() => void runSearch(term), debounceMs);
    return () => window.clearTimeout(id);
  }, [term, debounceMs, runSearch, initialLoad, nonce]);

  // Abort on unmount so a resolved-into-nothing component never sets state.
  useEffect(
    () => () => {
      controller.current?.abort();
      requestId.current += 1;
    },
    [],
  );

  return {
    options,
    truncated,
    loading,
    error,
    query: term,
    setQuery: setTerm,
    isBelowMinLength: term.length > 0 && term.length < minSearchLength,
    retry: () => setNonce((n) => n + 1),
  };
}

/* =============================================================================
   useDebouncedValue
   A plain debounce for values that are not part of a remote search.
   ============================================================================= */

export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}
